import { Dispatch } from 'react-redux';
import { Action } from '../../actions';
import { ListDisplayType } from './constants';
import { Api } from '../../typings/api';
import { StoreState } from '../../reducers';
import { getDriveItemStatusFromMap, DriveItemFetchState, DriveListState, FetchStatus, FolderState } from './reducer';
import { requestMobileAppDownload, deleteItem } from '../DownloadManager/actions';
import { DownloadMode } from '../DownloadManager/reducer';
import { RouterParamsState } from '../SetRouteParams/reducer';
import NativeApi from '../../utils/nativeApi';
import UrlUtils from '../../utils/url';
import UserAgentUtils from '../../utils/userAgent';
import DriveItemUtils from '../../utils/driveItem';
import Utils from '../../utils';
import ApiUtils from '../../utils/api';
import { DriveItemListOrigin } from '../DriveItemListPage/constants';
import { createNotification } from '../NotificationBar/actions';
import RoutesUtils from '../../utils/routes';
import LocalStorageManager from '../../utils/localStorage/manager';
import DownloadManagerUtils from '../../utils/downloadManager';
import StatsUtils from '../../utils/statistics';
import { updateLastVersionRead } from '../ReadStatus/actions';
import store from '../../store';
import { downloadThumb } from '../OfflineManager/actions';
import { DownloadPriority } from '../../utils/nativeApi';
import RouteUtils from '../../utils/routes';

enum DriveItemListActionKeys {
	REQUEST_DRIVE_META = '@@driveList/REQUEST_DRIVE_META',
	RECEIVE_DRIVE_META = '@@driveList/RECEIVE_DRIVE_META',
	FETCH_ERROR_DRIVE_META = '@@driveList/FETCH_ERROR_DRIVE_META',
	REQUEST_CONTENT_BLOCK = '@@driveList/REQUEST_CONTENT_BLOCK',
	RECEIVE_CONTENT_BLOCK = '@@driveList/RECEIVE_CONTENT_BLOCK',
	FETCH_ERROR_CONTENT_BLOCK = '@@driveList/FETCH_ERROR_CONTENT_BLOCK',
	RECEIVE_DRIVE_ITEMS = '@@driveList/RECEIVE_DRIVE_ITEMS',
	REMOVE_DRIVE_ITEMS = '@@driveList/REMOVE_DRIVE_ITEMS',
	RECEIVE_RESOLUTIONS = '@@driveList/RECEIVE_RESOLUTIONS',
	UPDATE_LIST_DISPLAY_TYPE = '@@driveList/UPDATE_LIST_DISPLAY_TYPE',
	UPDATE_DRIVE_ITEM = '@@driveList/UPDATE_DRIVE_ITEM',
	REQUEST_ALL = '@@driveList/REQUEST_ALL',
	RECEIVE_ALL = '@@driveList/RECEIVE_ALL',
	OPEN_CONTEXTUAL_MENU = '@@driveList/OPEN_CONTEXTUAL_MENU',
	CLOSE_CONTEXTUAL_MENU = '@@driveList/CLOSE_CONTEXTUAL_MENU',
	SET_BREADCRUMBS = '@@driveList/SET_BREADCRUMBS',
	RESET_BREADCRUMBS = '@@driveList/RESET_BREADCRUMBS',
	TOGGLE_EDIT_MODE = '@@driveList/TOGGLE_EDIT_MODE',
	MOVE_DRIVE_ITEM = '@@driveList/MOVE_DRIVE_ITEM',
	MOVE_DRIVE_ITEM_IN_FOLDER = '@@driveList/MOVE_DRIVE_ITEM_IN_FOLDER',
	RESET_DRIVE_ORDER = '@@driveList/RESET_DRIVE_ORDER',
	SET_LOADING_STATUS = '@@driveList/SET_LOADING_STATUS',
	SET_FOLDER_DISPLAY_TYPE = '@@driveList/SET_FOLDER_DISPLAY_TYPE',
	SET_FOLDERS_DISPLAY_TYPE = '@@driveList/SET_FOLDERS_DISPLAY_TYPE',
	RESET_FOLDERS_DISPLAY_TYPE = '@@driveList/RESET_FOLDERS_DISPLAY_TYPE',
	SET_CUSTOM_TEMPLATES_IDS = '@@driveList/SET_CUSTOM_TEMPLATES_IDS',
	ADD_CUSTOM_TEMPLATE = '@@driveList/ADD_CUSTOM_TEMPLATE',
	REMOVE_CUSTOM_TEMPLATE = '@@driveList/REMOVE_CUSTOM_TEMPLATE',
}

interface RequestDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.REQUEST_DRIVE_META;
	driveItemId: number;
}

interface ReceiveDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_DRIVE_META;
	driveItemId: number;
}

interface FetchErrorDriveItemMetaAction extends Action {
	type: DriveItemListActionKeys.FETCH_ERROR_DRIVE_META;
	driveItemId: number;
}

interface RequestContentBlockAction extends Action {
	type: DriveItemListActionKeys.REQUEST_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveContentBlockAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveDriveItemsAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS;
	driveItems: { [k: number]: Api.DriveItem };
	isInGallery?: boolean;
}

interface RemoveDriveItemsAction extends Action {
	type: DriveItemListActionKeys.REMOVE_DRIVE_ITEMS;
	driveItemIds: number[];
}

interface FetchErrorContentBlockAction extends Action {
	type: DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK;
	driveFolderId: number;
	block: number;
}

interface ReceiveResolutionsAction extends Action {
	type: DriveItemListActionKeys.RECEIVE_RESOLUTIONS;
	resolutions: { [k: string]: Api.ResolutionGroup };
}

interface UpdateListDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.UPDATE_LIST_DISPLAY_TYPE;
	displayType: ListDisplayType;
}

interface UpdateDriveItemAction extends Action {
	type: DriveItemListActionKeys.UPDATE_DRIVE_ITEM;
	item: Api.DriveItem;
}

interface RequestAllDriveItems extends Action {
	type: DriveItemListActionKeys.REQUEST_ALL;
}

interface ReceiveAllDriveItems extends Action {
	type: DriveItemListActionKeys.RECEIVE_ALL;
}

interface OpenContextualMenuAction extends Action {
	type: DriveItemListActionKeys.OPEN_CONTEXTUAL_MENU;
	driveItemId: number;
}

interface CloseContextualMenuAction extends Action {
	type: DriveItemListActionKeys.CLOSE_CONTEXTUAL_MENU;
}

interface SetBreadcrumbsAction extends Action {
	type: DriveItemListActionKeys.SET_BREADCRUMBS;
	item: FolderState;
}

interface ResetBreadcrumbsAction extends Action {
	type: DriveItemListActionKeys.RESET_BREADCRUMBS;
}

interface ToggleEditModeAction extends Action {
	type: DriveItemListActionKeys.TOGGLE_EDIT_MODE;
}

interface MoveDriveItemAction extends Action {
	type: DriveItemListActionKeys.MOVE_DRIVE_ITEM;
	folderId: number;
	from: number;
	to: number;
}

interface MoveDriveItemInFolderAction extends Action {
	type: DriveItemListActionKeys.MOVE_DRIVE_ITEM_IN_FOLDER;
	folderId: number;
	id: number;
}

interface ResetDriveOrderAction extends Action {
	type: DriveItemListActionKeys.RESET_DRIVE_ORDER;
	folderId: number;
	resources: Api.DriveItemResource[];
}

interface SetLoadingStatusAction extends Action {
	type: DriveItemListActionKeys.SET_LOADING_STATUS;
	isLoading: boolean;
}

interface SetFolderDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.SET_FOLDER_DISPLAY_TYPE;
	folderId: number | string;
	displayType: ListDisplayType;
}

interface SetFoldersDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.SET_FOLDERS_DISPLAY_TYPE;
	data: { [k: string]: ListDisplayType };
}

interface ResetFoldersDisplayTypeAction extends Action {
	type: DriveItemListActionKeys.RESET_FOLDERS_DISPLAY_TYPE;
}

interface SetCustomTemplatesIdsAction extends Action {
	type: DriveItemListActionKeys.SET_CUSTOM_TEMPLATES_IDS;
	ids: number[];
}

interface AddCustomTemplateAction extends Action {
	type: DriveItemListActionKeys.ADD_CUSTOM_TEMPLATE;
	id: number;
}

interface RemoveCustomTemplateAction extends Action {
	type: DriveItemListActionKeys.REMOVE_CUSTOM_TEMPLATE;
	id: number;
}

type DriveItemListAction =
	| RequestDriveItemMetaAction
	| ReceiveDriveItemMetaAction
	| FetchErrorDriveItemMetaAction
	| RequestContentBlockAction
	| ReceiveContentBlockAction
	| FetchErrorContentBlockAction
	| ReceiveDriveItemsAction
	| ReceiveResolutionsAction
	| RemoveDriveItemsAction
	| UpdateListDisplayTypeAction
	| UpdateDriveItemAction
	| RequestAllDriveItems
	| ReceiveAllDriveItems
	| OpenContextualMenuAction
	| CloseContextualMenuAction
	| SetBreadcrumbsAction
	| ResetBreadcrumbsAction
	| ToggleEditModeAction
	| MoveDriveItemAction
	| MoveDriveItemInFolderAction
	| ResetDriveOrderAction
	| SetLoadingStatusAction
	| SetFolderDisplayTypeAction
	| SetFoldersDisplayTypeAction
	| ResetFoldersDisplayTypeAction
	| SetCustomTemplatesIdsAction
	| AddCustomTemplateAction
	| RemoveCustomTemplateAction;

enum DriveItemOrigin {
	List = 'list',
	Details = 'details',
	Menu = 'menu'
}

const fetchDriveItem = (driveItemId: number, driveFolderId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | Api.DriveItem> => {
	const { driveList } = getState();

	if (!driveList.driveItems[driveItemId]) {
		if (driveFolderId && driveList.driveItems[driveFolderId]) {
			const driveFolder = driveList.driveItems[driveFolderId];
			const resource = driveFolder.resources ? driveFolder.resources.filter(resource => resource.id === driveItemId)[0] : undefined;

			if (resource) {
				return dispatch(fetchContentBlock(driveFolderId, resource.block));
			}
		}
		return dispatch(getOrFetchDriveItemMeta(driveItemId));
	}
	return Promise.resolve(driveList.driveItems[driveItemId]);
};

let getAllDriveItemsPromise: Promise<number[]>;

const getAllDriveItems = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<number[]> => {
	const { driveList, galleryInfos } = getState();

	if (driveList.fetchAllFetchStatus === FetchStatus.NOT_FETCHED) {
		dispatch({ type: DriveItemListActionKeys.REQUEST_ALL });

		const { applicationGallery } = galleryInfos.config;
		const initChildrenPromise = dispatch(initChildren());

		getAllDriveItemsPromise = initChildrenPromise
			.then(() => {
				dispatch({ type: DriveItemListActionKeys.RECEIVE_ALL });

				const { driveList: { driveItems } } = getState();
				return Object.keys(driveItems)
					.map(id => parseInt(id, 10))
					.filter(id => id !== applicationGallery);
			});
	}
	return getAllDriveItemsPromise;
};

const initChildren = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const { appInfos } = getState();
	const isMultiApp = Utils.isMultiApp(appInfos);
	return isMultiApp
		? ApiUtils.getAppMeta()
			.then((appMeta: Api.NestedAppMeta) => {
				if (appMeta) {
					return dispatch(receiveNestedDriveItems(appMeta.app))
						.then(() => appMeta.sharedDir && dispatch(receiveNestedDriveItems(appMeta.sharedDir)));
				}
				return {};
			})
		: ApiUtils.getChildrenMeta()
			.then((childrenMeta: Api.NestedDriveItems) => {
				if (childrenMeta) {
					return dispatch(receiveNestedDriveItems(childrenMeta));
				}
				return {};
			})
		;
};

const receiveNestedDriveItems = (nestedDriveItems: Api.NestedDriveItems) =>
	(dispatch: Dispatch<StoreState>): Promise<{}> => {

		return dispatch(receiveDriveItemFullMeta(nestedDriveItems))
			.then(() => {
				const { resources = [] } = nestedDriveItems;
				const resourceReceivedPromises = Promise.all(resources.map(resource => dispatch(receiveNestedDriveItems(resource))));

				return resourceReceivedPromises;
			});
	};

const receiveDriveItemFullMeta = (nestedDriveItems: Api.NestedDriveItems) =>
	(dispatch: Dispatch<StoreState>): Promise<void> => {
		// clone nestedDriveItems but removes resolutions
		let { resolutions, ...nestedDriveItemsWithoutResolutions } = nestedDriveItems;
		let resources: Api.DriveItemResource[] | undefined;
		if (nestedDriveItems.resources) {
			const { resources: nestedDriveItemsResources } = nestedDriveItems;
			resources = nestedDriveItemsResources.map(({ id, type, position }) => ({
				id,
				type,
				position,
			}));
		}

		const driveItem: Api.DriveItem | Api.DriveFolderInfo = {
			...nestedDriveItemsWithoutResolutions,
			resources
		};

		dispatch(receiveDriveItems({
			[driveItem.id]: driveItem
		}));
		if (resolutions) {
			dispatch(receiveResolutions(resolutions));
		}

		return Promise.resolve();
	};

const canMetaBeFetched = (driveList: DriveItemFetchState, forceFetch: boolean): boolean => {
	return driveList.metaFetchStatus === FetchStatus.NOT_FETCHED
		|| forceFetch && driveList.metaFetchStatus === FetchStatus.FETCHED;
};

const getOrFetchDriveItemMeta = (driveItemId: number, isInGallery: boolean = true, isAsync: boolean = true, forceFetch: boolean = false) =>
									(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | Api.DriveItem> => {
	const { driveList, galleryInfos } = getState();
	const { applicationGallery: galleryId } = galleryInfos.config;
	const driveItemList = getDriveItemStatusFromMap(driveList.driveItemsFetchStatus, driveItemId);
	const driveItemMeta = driveList.driveItems[driveItemId];
	const driveItemFetchStatus = driveList.driveItemsFetchStatus[driveItemId];

	if (
		!forceFetch
		&& driveItemMeta && driveItemFetchStatus && driveItemFetchStatus.metaFetchStatus === FetchStatus.FETCHED
	) {
		return Promise.resolve(driveList.driveItems[driveItemId]);
	}

	if (canMetaBeFetched(driveItemList, forceFetch)) {
		dispatch(requestDriveItemMeta(driveItemId));

		return ApiUtils.getMeta(driveItemId, isAsync)
			.then(driveItem => {
				if (driveItem.resolutions) {
					dispatch(receiveResolutions(driveItem.resolutions));
				}
				dispatch(receiveDriveItems({ [driveItem.id]: driveItem }, isInGallery));
				dispatch(receiveDriveItemMeta(driveItemId));

				if (driveItem.id === galleryId) {
					dispatch(setAppDisplayType());
				}
				return driveItem;
			})
			.catch(error => {
				dispatch(fetchErrorDriveItemMeta(driveItemId));
				dispatch(createNotification('error.fetch_content_error', { id: driveItemId }));
				return Promise.reject({});
			});
	}

	const resolvedDriveItem = driveList.driveItems[driveItemId];
	return Promise.resolve(resolvedDriveItem || {});
};

const requestDriveItemMeta = (driveItemId: number): RequestDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.REQUEST_DRIVE_META
});

const receiveDriveItemMeta = (driveItemId: number): ReceiveDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.RECEIVE_DRIVE_META
});

const fetchErrorDriveItemMeta = (driveItemId: number): FetchErrorDriveItemMetaAction => ({
	driveItemId,
	type: DriveItemListActionKeys.FETCH_ERROR_DRIVE_META
});

const canDriveListBeFetched = (driveList: DriveItemFetchState, block?: number): boolean => {
	return block !== undefined && (!driveList.contentBlocksStatus[block] || driveList.contentBlocksStatus[block] === FetchStatus.NOT_FETCHED);
};

const fetchContentBlock = (driveFolderId: number, block: number = 1) =>
								(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const { driveList } = getState();
	const driveFolder = driveList.driveItems[driveFolderId];
	const driveItemList = getDriveItemStatusFromMap(driveList.driveItemsFetchStatus, driveFolderId);

	if (canDriveListBeFetched(driveItemList, block) && driveFolder) {
		const { resolutionsBlock } = driveFolder;

		// If the first block is not already loaded, we load it, because it contains the resolution
		if (block !== resolutionsBlock && canDriveListBeFetched(driveItemList, resolutionsBlock)) {
			dispatch(fetchContentBlock(driveFolderId, resolutionsBlock));
		}

		dispatch(requestContentBlock(driveFolderId, block));

		return ApiUtils.getContentBlock(driveFolderId, block)
			.then((json: Api.DriveItemContentBlock) => {
				if (json.resolutions) {
					// Only store default resolution if we have not stored it yet
					// Will cause problem is hte first folder fetched has custom resolution
					if (driveList.resolutions.default) {
						delete json.resolutions.default;
					}
					dispatch(receiveResolutions(json.resolutions));
				}
				dispatch(receiveDriveItems(json.metas));

				// download thumbnails for newly imported files in mobile app
				if (UserAgentUtils.isMobileApp) {
					const { offlineManager: { thumbMap } } = getState();

					Object.keys(json.metas)
						.filter(id => (
							!Object.keys(thumbMap).includes(id) &&
							json.metas[id].type !== Api.DriveItemType.Directory
						))
						.forEach(id => {
							dispatch(downloadThumb(parseInt(id, 10), DownloadPriority.Normal, false));
						});
				}

				return dispatch(receiveContentBlock(driveFolderId, json, block));
			})
			.catch(error => {
				dispatch(fetchErrorContentBlock(driveFolderId, block));
				dispatch(createNotification('error.fetch_content_block_error', { id: driveFolderId }));
				return {};
			});
	}
	return Promise.resolve({});
};

const requestContentBlock = (driveFolderId: number, block: number): RequestContentBlockAction => ({
	driveFolderId,
	type: DriveItemListActionKeys.REQUEST_CONTENT_BLOCK,
	block
});

const receiveContentBlock = (driveFolderId: number, json: Api.DriveItemContentBlock, block: number): ReceiveContentBlockAction => {
	const { metas } = json;
	let driveItems: Api.DriveItem[] = [];
	Object.keys(metas).forEach(key => driveItems.push(metas[key]));

	return ({
		driveFolderId,
		type: DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK,
		block
	});
};

const fetchErrorContentBlock = (driveFolderId: number, block: number): FetchErrorContentBlockAction => ({
	driveFolderId,
	type: DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK,
	block
});

const receiveDriveItems = (driveItems: { [k: number]: Api.DriveItem }, isInGallery: boolean = true) =>
	(dispatch: Dispatch<StoreState>): void => {
		const resolvedIsInGallery = !UserAgentUtils.isMobileApp ? undefined : isInGallery;

		const action: ReceiveDriveItemsAction = {
			type: DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS,
			driveItems,
			isInGallery: resolvedIsInGallery,
		};

		dispatch(action);
};

export const deleteFromAsp = (clientId: number, driveItem: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setLoadingStatus(true));
		const { undeletableResources } = await ApiUtils.trashResources(clientId, [driveItem.id]);
		const resourcesNotDeleted = Object.keys(undeletableResources);
		const itemToTrashIds = DriveItemUtils.getDriveItemChildren(driveItem.id);
		const itemDeletedIds = itemToTrashIds.filter(driveItemId => !resourcesNotDeleted.includes(driveItemId.toString()));
		const { driveList: { driveItems } } = getState();
		itemDeletedIds
			.map(itemDeletedId => driveItems[itemDeletedId])
			.forEach((itemDeleted => dispatch(deleteItem(itemDeleted))));
		dispatch(removeDriveItems(itemDeletedIds));

		if (resourcesNotDeleted.length > 0) {
			dispatch(createNotification('error.asset_not_erasable'));
		} else {
			// is needed to update 'resources' property of parent folder as after resource deleting structure of blocks is changed
			await dispatch(getOrFetchDriveItemMeta(driveItem.parent, true, true, true));
		}
	} catch (error) {
		dispatch(createNotification('error.cant_remove_asset'));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const removeDriveItems = (driveItemIds: { [k: number]: number }) => ({
	type: DriveItemListActionKeys.REMOVE_DRIVE_ITEMS,
	driveItemIds,
});

const receiveResolutions = (resolutions: { [k: string]: Api.ResolutionGroup }): ReceiveResolutionsAction => ({
	type: DriveItemListActionKeys.RECEIVE_RESOLUTIONS,
	resolutions
});

const updateDriveItem = (item: Api.DriveItem): UpdateDriveItemAction => ({
	type: DriveItemListActionKeys.UPDATE_DRIVE_ITEM,
	item,
});

const openContextualMenu = (driveItemId: number): OpenContextualMenuAction => ({
	type: DriveItemListActionKeys.OPEN_CONTEXTUAL_MENU,
	driveItemId,
});

const closeContextualMenu = (): CloseContextualMenuAction => ({
	type: DriveItemListActionKeys.CLOSE_CONTEXTUAL_MENU,
});

const updateDriveItemName = (assetId: number, newLabel: string) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {

		const { driveList: { driveItems } } = getState();
		const renamedResource = Utils.deepCopy(driveItems[assetId]);
		renamedResource.label = newLabel;
		dispatch(updateDriveItem(renamedResource));
	};

const updateListDisplayType = (displayType: ListDisplayType) =>
	(dispatch: Dispatch<StoreState>): void => {
	LocalStorageManager.setDisplayType(displayType);

	dispatch({
		type: DriveItemListActionKeys.UPDATE_LIST_DISPLAY_TYPE,
		displayType
	});

	StatsUtils.trackDisplayType(displayType);
};

const setReadStatus = (driveItemId: number) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { driveList, readStatus } = getState();
	const isUnread = DriveItemUtils.isUnread(driveList, readStatus, driveItemId);

	if (isUnread) {
		dispatch(updateLastVersionRead(driveItemId));
	}
};


interface PublicationOptions {
	page?: number;
	hideWelcomePage?: boolean;
}

const viewDriveItem = (driveItem: Api.DriveItem, origin: DriveItemOrigin, publicationOptions?: PublicationOptions) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {

	if (!driveItem) {
		dispatch(createNotification('error.open_fail'));
		return;
	}

	if (driveItem.endDate && Utils.parseDate(driveItem.endDate) < new Date()) {
		dispatch(createNotification('error.content_expired', { id: driveItem.id }));
		return;
	}

	let url: string;
	const { galleryInfos, routerParamsHistory, downloadManager, driveList } = getState();
	const galleryConfig = galleryInfos.config;
	const downloadMode = downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id].mode : undefined;
	const isAndroidApp = UserAgentUtils.isAndroidApp();

	if (driveItem.type !== Api.DriveItemType.Directory) {
		dispatch(setReadStatus(driveItem.id));
	}

	StatsUtils.trackView(driveItem);
	switch (driveItem.type) {
		case Api.DriveItemType.Directory:
			window.location.hash = `#${RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, driveItem.id.toString())}`;
			break;
		case Api.DriveItemType.Image:
		case Api.DriveItemType.Movie:
		case Api.DriveItemType.Sound:
			goToTheaterPage(driveItem, routerParamsHistory, galleryConfig, driveList, origin);
			break;
		case Api.DriveItemType.Publication:
			const {
				page = undefined,
				hideWelcomePage = undefined
			} = publicationOptions || {};
			url = UrlUtils.getPublicationUrl(galleryConfig, driveItem, page, UserAgentUtils.isMobileApp, false, hideWelcomePage);
			if (UserAgentUtils.isMobileApp) {
				if (DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id)) {
					const downloadFolderPath = Utils.getPersistentDownloadsFolder();
					const publicationPath = `${driveItem.id}/index.html`;
					const publicationUrlParams = UrlUtils.getPublicationUrlParams(page, UserAgentUtils.isMobileApp, hideWelcomePage);
					const localUrl = `${downloadFolderPath}/${publicationPath}${publicationUrlParams}`;
					NativeApi.openFrame(Utils.getUrlForSecuredResource(localUrl));
				} else {
					NativeApi.openFrame(url);
				}
			} else {
				window.open(url);
			}
			break;
		case Api.DriveItemType.File:
			const fileName = DriveItemUtils.getDownloadedFileName(driveItem);
			const isItemPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);
			const isItemDownloaded = isItemPersistentlyDownloaded || downloadMode === DownloadMode.Dependency; // Todo better name ?
			if (isItemDownloaded) {
				const downloadFolderPath = UserAgentUtils.isWindowsApp() ?
					Utils.getRelativeDownloadsFolder() :
					Utils.getPersistentDownloadsFolder()
				;
				url = `${downloadFolderPath}/${fileName}`;
			} else if (downloadMode === DownloadMode.Temp) {
				url = `${Utils.getTemporaryDownloadsFolder()}/${fileName}`;
			} else {
				url = UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
			}

			if (isAndroidApp && !downloadMode) {
				// Temporarily downloading the file before opening it in a third party app, to prevent Android crashes on recent versions
				dispatch(requestMobileAppDownload(driveItem, DownloadMode.Temp))
					.then(() => window.open(`${Utils.getTemporaryDownloadsFolder()}/${fileName}`));
			} else {
				if (UserAgentUtils.isWindowsApp() && isItemDownloaded) {
					NativeApi.openFileNatively(url);
				} else {
					window.open(url);
				}
			}
			break;
		case Api.DriveItemType.ExternalLink:
			window.open(driveItem.url, '_blank');
			break;
		case Api.DriveItemType.Component:
			window.location.hash = `#${RoutesUtils.getPathStaticPage(driveItem.id.toString())}`;
			break;
		default:
			// Types to ignore Template, V2Gateway, Link, AppContainer, Gallery
			break;
	}
};

const goToTheaterPage = (
	driveItem: Api.DriveItem,
	routerParamsHistory: RouterParamsState[],
	galleryConfig: Api.Config,
	driveList: DriveListState,
	source: DriveItemOrigin
): void => {
	const currentRoute = routerParamsHistory[routerParamsHistory.length - 1];
	const { params } = currentRoute;
	let origin;
	let originParam;

	if (source === DriveItemOrigin.List) {
		origin = (params.origin as DriveItemListOrigin) || DriveItemListOrigin.Folder;
		switch (origin) {
			case DriveItemListOrigin.Folder:
				originParam = params.originParam || galleryConfig.applicationGallery;
				break;
			case DriveItemListOrigin.Search:
			case DriveItemListOrigin.Assortment:
				originParam = params.originParam;
				break;
		}
	} else if (source === DriveItemOrigin.Details) {
		origin = params.origin as DriveItemListOrigin || DriveItemListOrigin.Folder;
		originParam = params.folderId || resolveParentId(driveItem.id, driveList);
	} else {
		origin = DriveItemOrigin.Menu;
	}

	window.location.hash = `#${RoutesUtils.getPathTheater(origin, originParam, driveItem.id)}`;
};

const resolveParentId = (driveItemId: number, driveList: DriveListState): number | void => {
	const { driveItems } = driveList;
	const driveItemsKeys = Object.keys(driveItems);

	for (let i = 0; i < driveItemsKeys.length; i++) {
		const folderId = parseInt(driveItemsKeys[i], 10);
		const folderMeta = driveItems[folderId];

		if (folderMeta && folderMeta.resources) {
			for (let j = 0; j < folderMeta.resources.length; j++) {
				const resource = folderMeta.resources[j];

				if (resource.id === driveItemId) {
					return folderId;
				}
			}
		}
	}
};

const setAppDisplayType = () => (dispatch: Dispatch<StoreState>) => {
	const storedDisplayType = LocalStorageManager.getDisplayType();
	const appDisplayType = storedDisplayType || ListDisplayType.Default;
	dispatch(updateListDisplayType(appDisplayType));
};

const setBreadcrumbs = (item: FolderState): SetBreadcrumbsAction => ({
	type: DriveItemListActionKeys.SET_BREADCRUMBS,
	item,
});

const resetBreadcrumbs = (): ResetBreadcrumbsAction => ({
	type: DriveItemListActionKeys.RESET_BREADCRUMBS,
});

const toggleEditMode = (): ToggleEditModeAction => ({
	type: DriveItemListActionKeys.TOGGLE_EDIT_MODE,
});

const getResourceId = (id: number): number => {
	const { driveList: { driveItems } } = store.getState();
	const { linkId } = driveItems[id];
	// if resource is linked we have to use linkId
	return linkId || id;
};

const moveDriveItem = (folderId: number, from: number, to: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		galleryInfos: { config: { clientId } },
		driveList: { driveItems },
	} = getState();
	const { resources } = driveItems[folderId];
	const isMoveToTop = to - from < 0;
	const { id } = resources![from];
	const resourceId = getResourceId(id);
	const newIndex = isMoveToTop ? to : to - 1;
	const { position } = resources![newIndex];
	const newPosition = isMoveToTop ? position as number + 1 : position as number;

	try {
		dispatch({
			type: DriveItemListActionKeys.MOVE_DRIVE_ITEM,
			folderId,
			from,
			to,
		});

		await ApiUtils.moveResourcesOrder(clientId, folderId, [resourceId], newPosition);
	} catch (error) {
		dispatch(createNotification('error.cant_move_asset'));
		dispatch(resetDriveOrder(folderId, resources as Api.DriveItemResource[]));
	}
};

const moveDriveItemInFolder = (folderId: number, id: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		galleryInfos: { config: { clientId, applicationGallery } },
		routerParamsHistory,
		driveList: { driveItems },
	} = getState();
	const currentFolderId = Utils.getCurrentFolderId(applicationGallery, routerParamsHistory);

	if (!currentFolderId) {
		return;
	}

	const { resources } = driveItems[currentFolderId];
	const resourceId = getResourceId(id);

	try {
		dispatch({
			type: DriveItemListActionKeys.MOVE_DRIVE_ITEM_IN_FOLDER,
			folderId: currentFolderId,
			id,
		});
		dispatch(setLoadingStatus(true));

		await ApiUtils.moveResources(clientId, folderId, [resourceId]);
		await Promise.all([
			dispatch(getOrFetchDriveItemMeta(currentFolderId, true, true, true)),
			dispatch(getOrFetchDriveItemMeta(folderId, true, true, true)),
			dispatch(getOrFetchDriveItemMeta(resourceId, true, true, true)),
		]);
	} catch (error) {
		dispatch(createNotification('error.cant_move_asset'));
		dispatch(resetDriveOrder(currentFolderId, resources as Api.DriveItemResource[]));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const resetDriveOrder = (folderId: number, resources: Api.DriveItemResource[]): ResetDriveOrderAction => ({
	type: DriveItemListActionKeys.RESET_DRIVE_ORDER,
	folderId,
	resources,
});

const setLoadingStatus = (isLoading: boolean): SetLoadingStatusAction => ({
	type: DriveItemListActionKeys.SET_LOADING_STATUS,
	isLoading,
});

const setFolderDisplayType = (folderId: number | undefined, displayType: ListDisplayType) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { routerParamsHistory } = getState();
	const folderIdOrOrigin = folderId || RouteUtils.getCurrentOrigin(routerParamsHistory);

	dispatch({
		type: DriveItemListActionKeys.SET_FOLDER_DISPLAY_TYPE,
		folderId: folderIdOrOrigin,
		displayType,
	});

	LocalStorageManager.setFolderDisplayType(folderIdOrOrigin, displayType);
};

const setFoldersDisplayType = () => (dispatch: Dispatch<StoreState>): void => {
	const foldersDispayType = LocalStorageManager.getFoldersDisplayType() || {};
	dispatch({
		type: DriveItemListActionKeys.SET_FOLDERS_DISPLAY_TYPE,
		data: foldersDispayType,
	});
};

const resetFoldersDisplayType = () => (dispatch: Dispatch<StoreState>): void => {
	dispatch({
		type: DriveItemListActionKeys.RESET_FOLDERS_DISPLAY_TYPE,
	});
	LocalStorageManager.resetFoldersDisplayType();
};

const getCustomTemplatesIds = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			driveList: { customTemplates: { isLoaded } },
		} = getState();


		if (isLoaded) {
			return;
		}

		dispatch(setLoadingStatus(true));
		const { customTemplates } = await ApiUtils.getProposalTemplates();
		const ids = customTemplates.map(({ id }) => id);
		dispatch(setCustomTemplatesIds(ids));
	} catch (error) {
		dispatch(createNotification('error.fetch_proposal_templates_error'));
	} finally {
		dispatch(setLoadingStatus(false));
	}
};

const setCustomTemplatesIds = (ids: number[]): SetCustomTemplatesIdsAction => ({
	type: DriveItemListActionKeys.SET_CUSTOM_TEMPLATES_IDS,
	ids,
});

const addCustomTemplate = (id: number): AddCustomTemplateAction => ({
	type: DriveItemListActionKeys.ADD_CUSTOM_TEMPLATE,
	id,
});

const removeCustomTemplate = (id: number): RemoveCustomTemplateAction => ({
	type: DriveItemListActionKeys.REMOVE_CUSTOM_TEMPLATE,
	id,
});

export {
	DriveItemListActionKeys,
	DriveItemListAction,
	DriveItemOrigin,
	fetchDriveItem,
	getAllDriveItems,
	getOrFetchDriveItemMeta,
	receiveDriveItems,
	receiveResolutions,
	updateListDisplayType,
	updateDriveItem,
	openContextualMenu,
	closeContextualMenu,
	PublicationOptions,
	viewDriveItem,
	resolveParentId,
	setReadStatus,
	setBreadcrumbs,
	resetBreadcrumbs,
	updateDriveItemName,
	toggleEditMode,
	moveDriveItem,
	moveDriveItemInFolder,
	resetDriveOrder,
	setLoadingStatus,
	setFolderDisplayType,
	setFoldersDisplayType,
	resetFoldersDisplayType,
	getCustomTemplatesIds,
	setCustomTemplatesIds,
	addCustomTemplate,
	removeCustomTemplate,
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/actions.ts